import { useState, useEffect } from "react";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  User,
  Palette,
  MessageSquare,
  Save,
  Eye,
  EyeOff,
  Webhook,
  Copy,
  RefreshCw,
  Check,
  Sparkles,
  Zap,
  Camera,
  Wallet,
  Bell,
  Phone,
  Lock,
  BarChart2
} from "lucide-react";
import { AIUsageDisplay } from "../components/dialer/DialerComponents";
import StripeConnectBanner from "../components/StripeConnectBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const [settings, setSettings] = useState({
    company_name: "",
    pdf_logo_url: "",
    pdf_accent_color: "#D4AF37",
    twilio_account_sid: "",
    twilio_auth_token: "",
    twilio_phone_number: ""
  });
  const [notifPhone, setNotifPhone]   = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [webhookConfig, setWebhookConfig] = useState({
    webhook_url: "",
    webhook_token: "",
    is_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [showWebhookToken, setShowWebhookToken] = useState(false);
  const [copied, setCopied] = useState(null);
  const [ceiPublic, setCeiPublic] = useState(true);
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  const tier = subscription?.tier || user?.subscription_tier || "starter";

  useEffect(() => {
    fetchSettings();
    fetchWebhookConfig();
    fetchProfile();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`, { withCredentials: true });
      setSettings({
        company_name: response.data.company_name || "",
        pdf_logo_url: response.data.pdf_logo_url || "",
        pdf_accent_color: response.data.pdf_accent_color || "#D4AF37",
        twilio_account_sid: response.data.twilio_account_sid || "",
        twilio_auth_token: response.data.twilio_auth_token || "",
        twilio_phone_number: response.data.twilio_phone_number || ""
      });
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/profile`, { withCredentials: true });
      setNotifPhone(res.data.phone || "");
      setCeiPublic(res.data.cei_public !== false); // default true
    } catch { /* silent */ }
  };

  const savePrivacy = async (newVal) => {
    setSavingPrivacy(true);
    try {
      await axios.put(`${BACKEND_URL}/api/profile`, { cei_public: newVal }, { withCredentials: true });
      setCeiPublic(newVal);
      toast.success(newVal ? "CEI score is now visible to your network" : "CEI score is now private");
    } catch {
      toast.error("Failed to update privacy setting");
    } finally { setSavingPrivacy(false); }
  };

  const saveNotifPhone = async () => {
    setSavingPhone(true);
    try {
      await axios.put(`${BACKEND_URL}/api/profile`, { phone: notifPhone }, { withCredentials: true });
      toast.success("Notification phone saved");
    } catch {
      toast.error("Failed to save phone number");
    } finally { setSavingPhone(false); }
  };

  const fetchWebhookConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/webhook/config`, { withCredentials: true });
      setWebhookConfig({
        webhook_url: `${BACKEND_URL}${response.data.webhook_url}`,
        webhook_token: response.data.webhook_token || "",
        is_enabled: response.data.is_enabled !== false
      });
    } catch (error) {
      console.error("Failed to fetch webhook config:", error);
    }
  };

  const regenerateWebhookToken = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/webhook/regenerate-token`, {}, { withCredentials: true });
      setWebhookConfig({ ...webhookConfig, webhook_token: response.data.webhook_token });
      toast.success("Webhook token regenerated");
    } catch (error) {
      toast.error("Failed to regenerate token");
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
    toast.success("Copied to clipboard");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Don't send masked token
      const payload = { ...settings };
      if (payload.twilio_auth_token && payload.twilio_auth_token.startsWith("***")) {
        delete payload.twilio_auth_token;
      }
      
      await axios.put(`${BACKEND_URL}/api/settings`, payload, { withCredentials: true });
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const presetColors = [
    "#D4AF37", // Gold
    "#3B82F6", // Blue
    "#10B981", // Emerald
    "#8B5CF6", // Purple
    "#EF4444", // Red
    "#EC4899", // Pink
    "#F59E0B", // Amber
    "#FFFFFF"  // White
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass} data-testid="settings-page">
        {/* System Overview Strip */}
        <header className="border-b border-white/[0.06] bg-[#0A0A0A]/80 backdrop-blur-sm">
          <div className="px-4 md:px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4 md:gap-6">
                <div>
                  <h1 className="text-base md:text-lg font-semibold tracking-tight">
                    System <span className="text-[#D4AF37]">Settings</span>
                  </h1>
                  <p className="text-[9px] text-white/50 uppercase tracking-[0.15em] hidden md:block">Account & Integration Configuration</p>
                </div>
                
                <div className="flex items-center gap-2 px-2 md:px-3 py-1 md:py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
                  <div className="relative w-2 h-2">
                    <div className="absolute inset-0 bg-emerald-400 rounded-full" />
                    <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
                  </div>
                  <span className="text-[10px] font-semibold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Connected</span>
                </div>
              </div>

              <button 
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold text-sm rounded-sm hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="save-settings-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="p-6 space-y-6 max-w-3xl">
            {/* Profile → moved to Network Hub */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#D4AF37]/20 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                    <Camera className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-white">Profile</h2>
                    <p className="text-[10px] text-white/50">Avatar, banner, bio, social links & more</p>
                  </div>
                </div>
                <a href="/network?tab=profile"
                  className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-semibold rounded-lg hover:bg-[#D4AF37]/20 transition-all">
                  Edit Profile →
                </a>
              </div>
              <p className="text-white/30 text-xs mt-3">Your public profile has moved to the <span className="text-[#D4AF37]">Network Hub → Profile tab</span> for a richer editing experience with banner uploads, social links, and activity feed.</p>
            </div>

            {/* Account Info Section */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Account Settings</h2>
                  <p className="text-[10px] text-white/50">Your business configuration</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[10px] text-white/40 uppercase tracking-wider">Company Name (for documents)</Label>
                  <Input
                    value={settings.company_name}
                    onChange={(e) => setSettings({...settings, company_name: e.target.value})}
                    className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
                    placeholder="Your Company LLC"
                  />
                </div>
              </div>
            </div>

            {/* PDF Customization */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                  <Palette className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">PDF Customization</h2>
                  <p className="text-[10px] text-white/50">Customize your deal packet appearance</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[10px] text-white/40 uppercase tracking-wider">Logo URL</Label>
                  <Input
                    value={settings.pdf_logo_url}
                    onChange={(e) => setSettings({...settings, pdf_logo_url: e.target.value})}
                    className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
                    placeholder="https://yoursite.com/logo.png"
                  />
                  <p className="text-[10px] text-white/40">Enter a URL to your company logo (recommended: PNG, transparent background)</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[10px] text-white/40 uppercase tracking-wider">Accent Color</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      type="color"
                      value={settings.pdf_accent_color}
                      onChange={(e) => setSettings({...settings, pdf_accent_color: e.target.value})}
                      className="w-16 h-10 p-1 bg-white/[0.02] border-white/[0.06] cursor-pointer"
                    />
                    <Input
                      value={settings.pdf_accent_color}
                      onChange={(e) => setSettings({...settings, pdf_accent_color: e.target.value})}
                      className="flex-1 bg-white/[0.02] border-white/[0.06] text-white font-mono focus:border-[#D4AF37]/30"
                      placeholder="#D4AF37"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-[10px] text-white/40">Presets:</span>
                    {presetColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setSettings({...settings, pdf_accent_color: color})}
                        className={`w-6 h-6 rounded-sm border-2 transition-all ${
                          settings.pdf_accent_color === color ? 'border-white scale-110' : 'border-white/[0.1]'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Webhook Integration */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                  <Webhook className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Inbound Lead Webhook</h2>
                  <p className="text-[10px] text-white/50">Auto-create deals from your funnels and marketing tools</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[10px] text-white/40 uppercase tracking-wider">Webhook URL</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={webhookConfig.webhook_url}
                      readOnly
                      className="bg-white/[0.02] border-white/[0.06] text-white font-mono text-sm flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => copyToClipboard(webhookConfig.webhook_url, 'url')}
                      className="px-3 py-2 border border-white/[0.06] text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/30 rounded-sm transition-all duration-300"
                    >
                      {copied === 'url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[10px] text-white/40 uppercase tracking-wider">Webhook Token (X-Webhook-Token header)</Label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Input
                        type={showWebhookToken ? "text" : "password"}
                        value={webhookConfig.webhook_token}
                        readOnly
                        className="bg-white/[0.02] border-white/[0.06] text-white font-mono text-sm pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowWebhookToken(!showWebhookToken)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-all duration-300"
                      >
                        {showWebhookToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(webhookConfig.webhook_token, 'token')}
                      className="px-3 py-2 border border-white/[0.06] text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/30 rounded-sm transition-all duration-300"
                    >
                      {copied === 'token' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={regenerateWebhookToken}
                      className="px-3 py-2 border border-white/[0.06] text-white/50 hover:text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] hover:border-amber-500/30 rounded-sm transition-all duration-300"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="bg-black/80 border border-white/[0.06] rounded-lg p-4 mt-4">
                  <h4 className="text-sm font-medium text-white mb-2">How to use this webhook:</h4>
                  <ol className="text-[11px] text-white/50 space-y-1 list-decimal list-inside">
                    <li>Copy the webhook URL and token above</li>
                    <li>In your funnel/marketing tool, create a webhook that POSTs to this URL</li>
                    <li>Add the header: <code className="text-[#D4AF37] bg-white/[0.02] px-1 rounded">X-Webhook-Token: [your_token]</code></li>
                    <li>Send JSON with: property_address, owner_name, owner_phone, owner_email</li>
                  </ol>
                  
                  <div className="mt-4 p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg">
                    <p className="text-xs text-white/70 mb-2">Example payload:</p>
                    <pre className="text-xs text-gold font-mono overflow-x-auto">
{`{
  "property_address": "123 Main St, City, ST 12345",
  "owner_name": "John Smith",
  "owner_phone": "+15551234567",
  "owner_email": "john@example.com",
  "lead_source": "Facebook Ads"
}`}
                    </pre>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Settings - Stripe Connect */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-emerald-500/[0.08] border border-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Payment Settings</h2>
                  <p className="text-[10px] text-white/50">Connect your bank account to receive payments through DealForge</p>
                </div>
              </div>
              
              {/* Stripe Connect Banner */}
              <StripeConnectBanner 
                partnerType="investor" 
                apiPrefix="/api/auth"
              />
              
              {/* Payment Info */}
              <div className="mt-6 pt-6 border-t border-white/[0.06]">
                <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-4">How Payments Work</h3>
                <div className="grid gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D4AF37] font-bold text-sm">1</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Connect Stripe</p>
                      <p className="text-white/50 text-xs">Link your bank account securely through Stripe</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D4AF37] font-bold text-sm">2</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Close Deals</p>
                      <p className="text-white/50 text-xs">Complete transactions through the DealForge network</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-emerald-400 font-bold text-sm">3</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Get Paid</p>
                      <p className="text-white/50 text-xs">Receive distributions and returns directly to your bank</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Twilio Integration */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">SMS Integration</h2>
                  <p className="text-[10px] text-white/50">Connect your Twilio account for SMS outreach</p>
                </div>
              </div>
              
              {tier === "starter" ? (
                <div className="text-center py-6 bg-black/80 border border-dashed border-white/[0.1] rounded-lg">
                  <p className="text-white/50 mb-2">SMS blast is a Pro feature</p>
                  <a href="/upgrade" className="text-[#D4AF37] hover:text-[#F2D06B] text-sm">
                    Upgrade to Pro →
                  </a>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[10px] text-white/40 uppercase tracking-wider">Twilio Account SID</Label>
                    <Input
                      value={settings.twilio_account_sid}
                      onChange={(e) => setSettings({...settings, twilio_account_sid: e.target.value})}
                      className="bg-white/[0.02] border-white/[0.06] text-white font-mono focus:border-[#D4AF37]/30"
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[10px] text-white/40 uppercase tracking-wider">Twilio Auth Token</Label>
                    <div className="relative">
                      <Input
                        type={showTwilioToken ? "text" : "password"}
                        value={settings.twilio_auth_token}
                        onChange={(e) => setSettings({...settings, twilio_auth_token: e.target.value})}
                        className="bg-white/[0.02] border-white/[0.06] text-white font-mono pr-10 focus:border-[#D4AF37]/30"
                        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      />
                      <button
                        type="button"
                        onClick={() => setShowTwilioToken(!showTwilioToken)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-all duration-300"
                      >
                        {showTwilioToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[10px] text-white/40 uppercase tracking-wider">Twilio Phone Number</Label>
                    <Input
                      value={settings.twilio_phone_number}
                      onChange={(e) => setSettings({...settings, twilio_phone_number: e.target.value})}
                      className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
                      placeholder="+15551234567"
                    />
                  </div>
                  
                  <div className="bg-black/80 border border-white/[0.06] rounded-lg p-4 mt-4">
                    <h4 className="text-sm font-medium text-white mb-2">How to get Twilio credentials:</h4>
                    <ol className="text-[11px] text-white/50 space-y-1 list-decimal list-inside">
                      <li>Create a Twilio account at <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">twilio.com</a></li>
                      <li>Find your Account SID and Auth Token in the Console Dashboard</li>
                      <li>Purchase a phone number or use an existing one</li>
                      <li>Enter the credentials above and save</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>

            {/* Milestone Alerts Section */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                  <Bell className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Milestone Alerts</h2>
                  <p className="text-[10px] text-white/50">Receive SMS when deal milestones are due or overdue</p>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] text-white/40 uppercase tracking-wider flex items-center gap-1.5">
                  <Phone className="w-3 h-3" /> Your Notification Phone
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={notifPhone}
                    onChange={e => setNotifPhone(e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    data-testid="notification-phone-input"
                    className="flex-1 bg-white/[0.02] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/30"
                  />
                  <button
                    onClick={saveNotifPhone}
                    disabled={savingPhone}
                    data-testid="save-notification-phone-btn"
                    className="px-4 py-2 bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black text-xs font-bold rounded-lg disabled:opacity-50 transition-all"
                  >
                    {savingPhone ? "Saving..." : "Save"}
                  </button>
                </div>
                <p className="text-[10px] text-white/30">
                  Enter in E.164 format, e.g. +12145550100. This is where milestone alerts are sent — not your Twilio number.
                </p>
              </div>
            </div>

            {/* Privacy & Visibility */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Privacy & Visibility</h2>
                  <p className="text-[10px] text-white/50">Control what others can see on your network profile</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-black/40 border border-white/[0.06] rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${ceiPublic ? "bg-[#D4AF37]/10 border border-[#D4AF37]/20" : "bg-white/[0.04] border border-white/[0.08]"}`}>
                    <BarChart2 className={`w-4 h-4 ${ceiPublic ? "text-[#D4AF37]" : "text-white/30"}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">CEI Score Visibility</p>
                    <p className="text-[10px] text-white/40 mt-0.5">
                      {ceiPublic
                        ? "Your CEI score is visible on the leaderboard and to your network"
                        : "Your CEI score is hidden — only you can see it"}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => savePrivacy(!ceiPublic)}
                  disabled={savingPrivacy}
                  data-testid="cei-visibility-toggle"
                  className={`relative w-11 h-6 rounded-full transition-all duration-300 flex-shrink-0 ${
                    ceiPublic ? "bg-[#D4AF37]" : "bg-white/[0.1]"
                  } ${savingPrivacy ? "opacity-50" : ""}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-black rounded-full shadow transition-all duration-300 ${
                    ceiPublic ? "left-[22px]" : "left-0.5"
                  }`} />
                </button>
              </div>

              <p className="text-[10px] text-white/25 mt-3 px-1">
                When private, your CEI score shows as "Private" to other network members. Your own dashboard always shows your full score.
              </p>
            </div>

            {/* AI Usage Section */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">AI Usage</h2>
                  <p className="text-[10px] text-white/50">Track your monthly AI analysis credits</p>
                </div>
              </div>
              
              {tier === "starter" ? (
                <div className="text-center py-6 bg-black/80 border border-dashed border-white/[0.1] rounded-lg">
                  <Zap className="w-8 h-8 text-white/30 mx-auto mb-2" />
                  <p className="text-white/50 mb-2">AI features are available on Pro and above</p>
                  <a href="/upgrade" className="text-[#D4AF37] hover:text-[#F2D06B] text-sm">
                    Upgrade to Pro →
                  </a>
                </div>
              ) : (
                <AIUsageDisplay compact={false} />
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
